import {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Button, FormLabel, makeStyles, TextField, withStyles} from "@material-ui/core";
import {useDispatch, useSelector} from "react-redux";
import PillSection from "./pills";
import clsx from 'clsx';
import BackgroundLines from "./graph/bglines";
import {enableHorizontalScroll} from "../../../../../../helper/hooks/enableHorizontalScroll";
import ScissorMarker from "./notes/scissorMarker";
import InputMask from 'react-input-mask';
import ReactCursorPosition from 'react-cursor-position';
import CoreAnalysisLineBarGraph from './graph/linebar';
import UtilService from '../../../../../../helper/service';
import ImmersionModal from '../../../../../atoms/modals/commonModal';
import {
  getSegmentAsClip,
  initAnalysisData,
  onCallClipAPI,
  optimizeLiveGraph,
  saveCurrentSession,
  updateClipedData,
  updateSettings,
  updateTrimTime
} from '../../../../../../store/actions/creator/analysis';
import {apiClient} from '../../../../../../config/apiClient';
import VideoSection from '../live/video';
import AnalysisScoreIcon from '../../../../../atoms/creator/analyzeScoreIcon';
import {
  getSessionParticipants,
  updateSessionData
} from '../../../../../../store/actions/creator/creatorExperienceManager';
import {BackDropAnalyzeLoading,} from '../../../../../atoms/commonLoadings';

const useStyles = makeStyles(() => ({
  root: {
    positon: 'relative',
    marginTop: 45
  },
  title: {
    display: 'flex',
    position: 'absolute',
    color: '#000',
    fontSize: 20,
    flexDirection: 'column',
    fontFamily: 'roboto-light',
    opacity: 0.85,
    alignSelf: 'center',
    top: 100
  },
  headerTitle: {
    alignSelf: 'center',
    '& b': {
      fontFamily: 'roboto-bold'
    }
  },
  container: {
    position: 'relative',
    overflowY: 'hidden',
    overflowX: 'auto',
    paddingTop: 40,
    transition: '1s',
    scrollbarWidth: 'none',
  },
  videoBox: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  clipTitle: {
    fontFamily: 'Spartan-bold',
    fontSize: 24,
    color: '#697683',
    margin: '20px 0 20px 70px'
  },
  toTitle: {
    fontFamily: 'roboto-bold',
    fontSize: 20,
    color: '#697683',
    margin: '8px 12px',
  },
  textField: {
    border: '1px solid grey',
    paddingLeft: 12,
    borderRadius: 4,
    maxWidth: 105,
    background: 'transparent',
    fontFamily: 'roboto-light',
    fontSize: 20,
    color: '#017AEF',
    '& .MuiInputBase-input': {
      fontFamily: 'roboto-light',
      fontSize: 20,
      color: '#017AEF',
    }
  },
  trimBtn: {
    textTransform: 'none',
    height: 40,
    position: 'absolute',
    right: 12,
    bottom: 25,
    marginBottom: 12
  },
  label: {
    fontSize: 12,
    fontFamily: 'roboto-bold',
    color: '#697683',
    textAlign: 'left',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontFamily: 'roboto-bold',
    color: '#1DB859',
    fontSize: 12,
    margin: 0,
    '& p:first-of-type': {
      margin: '0 7px',
      fontSize: 18,
    },
    '& p:nth-of-type(2)': {
      fontFamily: 'Spartan-bold',
      letterSpacing: 1.2,
      paddingTop: 4,
      textTransform: 'uppercase',
    }
  },
  smallRow: {
    marginTop: 4,
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    fontFamily: 'roboto-bold',
    color: '#8191A1',
    '& img': {
      width: 12,
      height: 12,
      marginRight: 3
    },
    '& span': {
      marginRight: 3
    }
  },
  pane: {
    fontFamily: 'Spartan-bold',
    fontSize: 10,
    color: '#8191A1',
    marginTop: 6
  },
}));

const CssTextField = withStyles({
  root: {
    '& label.MuiFormLabel-root': {
      color: '#697683',
      fontFamily: 'roboto-bold',
      fontSize: 12,
    },
    '& .MuiInputBase-input': {
      fontFamily: 'roboto-regular',
      fontSize: 14,
      color: '#8F99A3'
    }
  },
})(TextField);

function ClipSection(props: any) {

  const classes = useStyles();
  const dispatch = useDispatch();
  const scrollRef: any = useRef();
  const activeScissorRef: any = useRef();
  const inactiveScissorRef: any = useRef();
  const loginUserData = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || "{}") : ""
  const apolloClient = useSelector((state: any) => state.apolloClient);

  const { currentSession, cliped, analyseStart, analyseEnd, identifiers, stimulus, clips, settings } = useSelector((state: any) => state.analysis);
  const { getMaxWidth, all } = props;
  const { graphZoom, videoRemoved } = settings;

  const { session_code, session_type, session_trim_from, session_trim_to, started_at, ended_at, scheduled_end_at } = currentSession;

  const [startTime, setStartTime] = useState(':');
  const [endTime, setEndTime] = useState(':');
  const [openTrimModal, setOpenTrimModal] = useState(false);
  const [clipCreateModal, setClipCreateModal] = useState<boolean>(false);
  const [clipViewModal, setClipViewModal] = useState<boolean>(false);
  const [clipName, setClipName] = useState<string>('Clip ' + Number(clips.length + 1));
  const [loading, setLoading] = useState<boolean>(false);
  const [score, setScore] = useState<any>();
  const [newClipId, setNewClipId] = useState<any>();
  const [isTrimLoading, setIsTrimLoading] = useState<boolean>(false);
  
  const [inactiveXScrollPosition, setInactiveXScrollPosition] = useState(0);
  const [inactiveXPosition, setInactiveXPosition] = useState(0);
  const [textFocus, setTextFocus] = useState(true);

  const start = cliped ? cliped.start_clip - analyseStart : 0;
  const sTime = UtilService.getTimeInputLabel(startTime, false);
  const eTime = UtilService.getTimeInputLabel(endTime, true);
  const leftStime = sTime / Math.pow(graphZoom, 2);
  const analysed_end = Math.floor(ended_at ? ended_at : scheduled_end_at);

  let graphLength = analyseEnd - analyseStart;
  if (stimulus) {
    graphLength = stimulus.stimulus_video_length;
  }

  const videoStart = 0;
  const videoEnd = graphLength;

  useEffect(() => {
    if (!scrollRef.current) return;
    enableHorizontalScroll(scrollRef);
  }, [scrollRef]);

  useEffect(() => {
    setClipName('Clip ' + Number(clips.length + 1));
  }, [clips]);

  const scissorMarkerScroll = useCallback((scrollLeft: any) => {

    if (!activeScissorRef?.current) return;
    activeScissorRef.current.containerScrolled(Number(scrollLeft));

    if (!inactiveScissorRef?.current) return;
    inactiveScissorRef.current.containerScrolled(Number(scrollLeft));
  }, [activeScissorRef, inactiveScissorRef]);

  const containerMouseMove = useCallback((mouseXPosition: number) => {
    if (!activeScissorRef?.current) return;
    activeScissorRef.current.setMouseXPosition(mouseXPosition);

    if (!inactiveScissorRef?.current) return;
    inactiveScissorRef.current.setMouseXPosition(mouseXPosition);

  }, [scrollRef]);

  const onContainerMouseMove = useCallback((event: any) => {
    if (!scrollRef.current) return
    const boundingRect = scrollRef.current.getBoundingClientRect();
    containerMouseMove(Math.floor(Number(event.clientX)) - Math.floor(Number(boundingRect.left)))

  }, [containerMouseMove]);

  useEffect(() => {
    const divHorScroll = scrollRef.current;
    if (!divHorScroll) return

    divHorScroll.onscroll = () => {
      const scrollLeft = divHorScroll.scrollLeft;
      scissorMarkerScroll(scrollLeft);
    }
  }, [scissorMarkerScroll]);

  useEffect(() => {
    containerMouseMove(inactiveXPosition);
  }, [inactiveXPosition]);

  useEffect(() => {
    scissorMarkerScroll(inactiveXScrollPosition);
  }, [inactiveXScrollPosition]);


  const handleUserKeyPress = (event: any) => {
    if (event.keyCode === 13 && clipCreateModal ) {
      setTextFocus(false);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  });

  const doneClip = () => {
    dispatch(updateSettings('clip', false));
  };

  const handleRemoveClip = () => {
    // setStartTime('00:00:00');
    // setEndTime('00:00:00');

    getSessionClips();
    setOpenTrimModal(false);
    setClipViewModal(false);
  };

  const getSessionClips = () => {
    if (session_code && stimulus && session_type === 'ASYNC') {
      dispatch(getSegmentAsClip(loginUserData.customer_id, stimulus.stimulus_id))
    }
    if (session_code && session_type === 'MODERATED') {
      dispatch(getSegmentAsClip(loginUserData.customer_id, session_code))
    }
  };

  const handleViewClip = () => {
    dispatch(optimizeLiveGraph());

    // in this case, the function that is located in topbar component will fetch clips data
    dispatch(updateClipedData({
      title: clipName,
      start_clip: sTime / 15 + (session_type === 'ASYNC' ? 0 : analyseStart),
      end_clip: eTime / 15 + (session_type === 'ASYNC' ? 0 : analyseStart),
      clip_id: newClipId,
      session_code,
      score
    }));
    setStartTime(':');
    setEndTime('00:00:00');

    setOpenTrimModal(false);
    setClipViewModal(false);

    doneClip();

  };

  const onCreateNewClip = async () => {

    setLoading(true);

    const from_epoch = sTime / 15 + analyseStart;
    const from_clip = UtilService.calcVideoEnd(sTime / 15);

    const to_epoch = eTime / 15 + analyseStart;
    const to_clip = UtilService.calcVideoEnd(eTime / 15);

    const start_clip = (session_type === 'ASYNC') ? from_clip : String(from_epoch);
    const end_clip = (session_type === 'ASYNC') ? to_clip : String(to_epoch);

    const request = {
      stimulus_id: session_type === 'ASYNC' ? stimulus.stimulus_id : session_code,
      type: session_type === 'ASYNC' ? 'clip-async' : 'clip-moderated',
      session_code,
      clip: {
        title: clipName,
        start_clip,
        end_clip
      }
    };

    const request2 = {
      identifiers,
      session_started_at: session_trim_from ? Number(started_at) : analyseStart,
      session_completed_at: session_trim_to ? analysed_end : analyseEnd,
      tenant_id: Number(loginUserData.customer_id),
      session_code,
      from_epoch,
      to_epoch
    };

    const request3 = {
      identifiers,
      session_started_at: session_trim_from ? Number(started_at) : analyseStart,
      session_completed_at: session_trim_to ? analysed_end : analyseEnd,
      tenant_id: Number(loginUserData.customer_id),
      session_code,
      from_clip,
      to_clip,
      stimulus_id: stimulus?.stimulus_id
    };

    const type = session_type === 'ASYNC' ? 'async' : 'sync';
    let immersion = { data: [] };
    let safety = { data: [] };

    try {
      immersion = await apiClient(true).post(`/analysis/v2/${type}/index/immersion/${loginUserData.customer_id}/${session_code}`, session_type === 'ASYNC' ? { ...request3 } : { ...request2 });
    } catch (e) {
      setLoading(false);
    }

    try {
      safety = await apiClient(true).post(`/analysis/v2/${type}/index/safety/${loginUserData.customer_id}/${session_code}`, session_type === 'ASYNC' ? { ...request3 } : { ...request2 });
    } catch {
      setLoading(false);
    }

    setLoading(false);
    setLoading(true);

    dispatch(onCallClipAPI(request, loginUserData.customer_id, session_code, (res: any) => {
      setScore({ immersion: immersion.data, safety: safety.data });
      if (res) {
        setNewClipId(res.clip_id)
        setClipViewModal(true)
        setLoading(false);
        dispatch(updateSettings('videoPopIn', 0))
      }
    }));
  }

  const onTrimming = () => {
    setIsTrimLoading(true);
    const request = {
      session_code,
      start_time: UtilService.getEpochToTime(sTime / 15 + analyseStart),
      end_time: UtilService.getEpochToTime(eTime / 15 + analyseStart)
    }
    dispatch(updateTrimTime(apolloClient.client, request, (res: any) => {
      setOpenTrimModal(false);
      if (res) {
        const newSession = { ...currentSession }
        newSession.session_trim_from = (sTime / 15 + analyseStart).toString()
        newSession.session_trim_to = (eTime / 15 + analyseStart).toString();

        dispatch(initAnalysisData());
        dispatch(getSessionParticipants(apolloClient.client, { session_code }, (c: any) => {
          if (c && c.session) {
            dispatch(updateSessionData(c));
            dispatch(saveCurrentSession(c.session));
            setIsTrimLoading(false);
          }
        }));
      }else {
        setIsTrimLoading(false);
      }
    }));
  }

  const onInitialTime = () => {
    if (sTime !== -1 && eTime !== -1) {
      setStartTime(':');
      setEndTime(':');
    }
  }

  const onSetVideoPlayed = (e: any) => {
    stimulus && dispatch(updateSettings('videoPopIn', UtilService.getTimeInputLabel(e, false) / 15))
  }

  return (
    <div className={classes.root}>
      <BackDropAnalyzeLoading loading={isTrimLoading}/>
      <div
        className={classes.title}
        style={{ width: getMaxWidth }}
      >
        <div className={classes.headerTitle}>
          <b>Click & Drag </b>&nbsp;to select a segment<br />
          <div>(or enter times directly into form)</div>
        </div>

      </div>

      <div
        className={clsx(classes.container, 'custom_scroll')}
        ref={scrollRef}
        style={{ width: getMaxWidth, }}
        onMouseMove={(e: any) => onContainerMouseMove(e)}
        onClick={onInitialTime}
      >

        <ReactCursorPosition
          hoverDelayInMs={250}
          hoverOffDelayInMs={150}
          isEnabled={sTime === -1 || eTime === -1}
        >

          {
            sTime !== -1 &&
            <ScissorMarker
              graphZoom={graphZoom}
              ref={inactiveScissorRef}
              left={leftStime}
              right={eTime / Math.pow(graphZoom, 2)}
              finished={sTime !== -1 && eTime !== -1}
              onFirstPoint={() => { }}
              onSetVideoPlayed={() => { }}
            />
          }
          {
            <ScissorMarker
              graphZoom={graphZoom}
              moving={true}
              ref={activeScissorRef}
              onSetVideoPlayed={onSetVideoPlayed}
              onFirstPoint={(xPosition: any, xScrollPosition: any) => {
                if (sTime === -1) {
                  setStartTime(UtilService.getTimeLabelClip((xPosition + xScrollPosition) / 15, graphZoom, start));
                  setInactiveXScrollPosition(xScrollPosition);
                  setInactiveXPosition(xPosition);
                } else {
                  setEndTime(UtilService.getTimeLabelClip((xPosition + xScrollPosition) / 15, graphZoom, start));
                }
              }
              }
              right={eTime / Math.pow(graphZoom, 2)}
              finished={sTime !== -1 && eTime !== -1}
            />
          }

          <BackgroundLines
            getMaxWidth={getMaxWidth}
            lineNumber={getMaxWidth / 15}
            graphZoom={graphZoom}
          />

          <div style={{ marginTop: 1 }}>
            <CoreAnalysisLineBarGraph
              width={getMaxWidth}
              graphData={all[0].immersion}
              clip={true}
            />
            <div style={{ marginTop: 45 }}>
              <PillSection
                graphZoom={graphZoom}
                graphWidth={getMaxWidth}
                graphData={all[0]}
                clip={true}
              />
            </div>
          </div>

        </ReactCursorPosition>

      </div>

      <div className={classes.videoBox}>
        {
          stimulus && !videoRemoved &&
          <VideoSection
            mb={0}
            mt={30}
            width={241}
            height={151}
            video={stimulus?.play_url ? stimulus?.play_url : stimulus?.stimulus_link}
            img={(stimulus?.thumbnails && stimulus?.thumbnails.length > 0) ? stimulus?.thumbnails[0]?.url : ''}
            popOut={() => { }}
            videoStart={videoStart}
            videoEnd={videoEnd}
          />
        }
        <Box ml={3} mt={stimulus ? 5 : 3}>
          <p className={classes.clipTitle}>Create Clip</p>
          <Box flexDirection='row' display='flex' mb={4}>

            <InputMask
              value={startTime}
              onChange={(e: any) => setStartTime(e.target.value)}
              className={classes.textField}
              mask="99:99:99"
              maskChar=" "
            />

            <span className={classes.toTitle}> to </span>

            <InputMask
              value={endTime}
              onChange={(e: any) => setEndTime(e.target.value)}
              className={classes.textField}
              mask="99:99:99"
              maskChar=" "
            />

            {
              sTime !== -1 && eTime !== -1 && eTime > sTime &&
              <Button
                color='primary'
                variant='contained'
                style={{ textTransform: 'none', marginLeft: 12 }}
                onClick={() => setClipCreateModal(true)}
                autoFocus
              >
                Save Clip
              </Button>
            }

          </Box>
        </Box>

        {
          sTime !== -1 && eTime !== -1 && session_type === 'MODERATED' &&
          <Button
            variant='contained'
            className={classes.trimBtn}
            onClick={() => setOpenTrimModal(true)}
          >
            Trim Experience
          </Button>}

      </div>

      <ImmersionModal
        title={'Are you sure?'}
        description={'When you trim your Experience, measurements for the section NOT selected will be deleted and cannot be recovered.'}
        titleOK={'Yes, trim this Experience'}
        open={openTrimModal}
        onApply={onTrimming}
        onClose={handleRemoveClip}
      />

      <ImmersionModal
        open={clipCreateModal}
        title={'Name Your Clip'}
        description={'Give your clip a name then save it. Hint: You can always find your Clips in the “My Experiences” dashboard.'}
        titleOK={'Save'}
        cancelDisable={true}
        okDisable={!clipName}
        focused={textFocus}
        onClose={() => {
          setClipCreateModal(false)
        }
        }
        additional={
          <div style={{ margin: '40px 40px 40px 0' }}>
            <FormLabel className={classes.label}>
              Clip Name
            </FormLabel>
            <br />
            <CssTextField
              fullWidth
              name="clip"
              value={clipName}
              onChange={e => setClipName(e.target.value)}
              autoFocus
              onFocus={event => {
                event.target.select()
              }}

            />
          </div>
        }
        onApply={() => {
          setClipCreateModal(false)
          onCreateNewClip()
        }}
      />

      <ImmersionModal
        open={clipViewModal}
        title={'✂ ' + clipName.toUpperCase()}
        titleOK={'View Clip'}
        titleCancel={'Make Another Clip'}
        onClose={()=>{
          setStartTime(':');          
          setEndTime(':');
          handleRemoveClip();
        }}
        additional={
          <div style={{ margin: '10px 20px 20px 0' }}>
            {score && <div className={classes.row} style={{ color: UtilService.refactorGraphColor(score.immersion.color_indicator) }}>
              <AnalysisScoreIcon color={score.immersion.color_indicator} type={'immersion'} size={2} />
              <p>{score.immersion.immersion_index === 0 ? '--' : score.immersion.immersion_index}</p>
              <p>{score.immersion.immersion_index === 0 ? '' : score.immersion.context_indicator}</p>
            </div>}

            {score && (!(stimulus && stimulus.stimulus_video_length < 121 && session_type === 'ASYNC') || session_type === 'MODERATED') &&
              <div className={classes.row} style={{ color: UtilService.refactorGraphColor(score.safety.safety_index > 1 ? score.safety.color_indicator : 'gray'), marginTop: score.safety.safety_index > 1 ? -10 : 0 }}>
                <AnalysisScoreIcon color={score.safety.safety_index > 1 ? score.safety.color_indicator : 'gray'} type={'safety'} size={2} />
                <p>{score.safety.safety_index > 1 ? score.safety.safety_index : '--'}</p>
                <p>{score.safety.safety_index > 1 ? score.safety.context_indicator : ''}</p>
              </div>}

            <div className={classes.pane}>
              <div className={classes.smallRow}>
                <img src={'/assets/icons/participant/clock-deactive.svg'} alt={'icon'} />
                <span> {UtilService.getTimeLabel(eTime / 15 - sTime / 15, 1, start)}</span>
                <span style={{ fontFamily: 'roboto-regular', fontSize: 8 }}> RUNTIME</span>
              </div>
              <div className={classes.smallRow}>
                <img src={'/assets/icons/participant/clock-deactive.svg'} alt={'icon'} />
                <span> {startTime}</span>
                <span style={{ fontFamily: 'roboto-regular', fontSize: 8 }}> START TIME</span>
              </div>
              <div className={classes.smallRow}>
                <img src={'/assets/icons/participant/clock-deactive.svg'} alt={'icon'} />
                <span> {endTime}</span>
                <span style={{ fontFamily: 'roboto-regular', fontSize: 8 }}> END TIME</span>
              </div>
            </div>
          </div>
        }
        onApply={handleViewClip}
      />

      <BackDropAnalyzeLoading loading={loading} />

    </div>
  );
}

export default ClipSection;
